import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { CHAT, CHATGROUP, TECHGROUP, TECHSERVICES } from 'src/app/core/constants/feature-key.constants';
import { FeatureGuard } from 'src/app/core/guards/feature.guard';
import { ContractGuard } from '../../../core/guards/contract.guard';
import { IPromoSlot } from 'src/app/model/promos/IPromoSlot';
import { ADPResourceService } from 'digital-core';
import { ActivatedRoute } from '@angular/router';
import { UtilityService } from 'src/app/core/services/utility.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

@Component({
  selector: 'app-tech-services',
  templateUrl: './tech-services.component.html',
  styleUrls: ['./tech-services.component.scss']
})
export class TechServicesComponent implements OnInit, OnDestroy {
  //public servicesPage: IServices;
  public promoSlot: IPromoSlot;
  isExpired: boolean = false;
  isTechServicesChatEnabled: boolean = false;
  isTechServicesSpecialOffersEnabled: boolean = false;

  // GTM variabless
  public userEmail: string;
  public contractId: string;
  public userType: string;
  public usersOnContract: number;
  isLoaded: boolean = false;
  isActive: boolean = false;
  private destroy$ = new Subject<boolean>();

  offers: string[];
  mobileOffers: string[];

  public hasP360MobilePolicy: boolean = false;
  public hasP360HomeTechPolicy: boolean = false;
  public isNative: boolean =  false;
  public isSPOEEnabled: boolean = false;

  public remoteSessionMobileUrl: string = '';
  public remoteSessionHomeTechUrl: string = '';
  public isMobileSize: boolean = false;

  constructor(
    private loggedInUserSettings: LoggedInUserSettings,
    private googleAnalyticsService: GoogleAnalyticsService,
    private featureGuard: FeatureGuard,
    private contractGuard: ContractGuard,
    private activatedRoute: ActivatedRoute,
    private utilityService: UtilityService,
    private storageService: StorageService,
    private resourceService: ADPResourceService,
    private breakpointObserver: BreakpointObserver
  ) {
    this.breakpointObserver.observe([Breakpoints.XSmall]).subscribe((result) => {
      this.isMobileSize = result.matches;
    });
   }

  @HostListener('window:resize', ['$event'])
    onResize(event) {
      this.isMobileSize = window.innerWidth < 992;
  }

  ngOnInit(): void {
    this.hasP360MobilePolicy = this.storageService.getLocal<string>(`EprismContractsCount`) > '0';
    this.hasP360HomeTechPolicy = this.storageService.getLocal<string>(`ElitaContractsCount`) > '0';
    this.isSPOEEnabled  = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
    this.activatedRoute.queryParams.subscribe((params) => {
      this.isNative = (params.isNative && params.isNative.toLowerCase().trim() === 'true') || this.utilityService.isNativeApp;
    });
    const joined$ = combineLatest(
      [
        this.loggedInUserSettings.$selectedContract,
        this.loggedInUserSettings.$userClaims,
      ]
    ).pipe(
      takeUntil(this.destroy$),
      filter(([a, b]) => a !== null && b !== null)
    );

    joined$.subscribe(([selectedContract, userClaims]: [ISelectedContract, UserClaims]) => {
      this.userEmail = userClaims.email;
      this.contractId = selectedContract.id;
      this.userType = this.googleAnalyticsService.isUserPrimary(selectedContract, userClaims) ? 'Primary' : 'Secondary';
      this.usersOnContract = selectedContract.members.length;
      this.isActive = this.contractGuard.activateGuard(selectedContract);

      // Check For sitefeature when bug is given again in future
      setTimeout(() => {
        this.setVisibility(selectedContract.dealerId);
      }, 0);
    });
    this.offers = this.getSetting('TechServices_Offerings').split(';');

    if(this.isSPOEEnabled && this.hasP360MobilePolicy){
      this.mobileOffers = this.getSetting('TechServices_Mobile_Offerings').split(';');
    }

    this.promoSlot = {
      heading: 'TechServices_Promo_Heading',
      promoText: 'TechServices_Promo_Text',
      secondaryHeader: 'TechServices_Promo_Secondary_Header',
      secondaryText: 'TechServices_Promo_Secondary_Text',
      imageKey: 'Global_TechServices_Promo_TechSupport_ImageKey',
      callToActionLabel: 'TechServices_Promo_CallToAction_Button_Label',
      isPromoAppType: this.resourceService.isPortalSettingTrue("TechServices_Promo_IsPromoAppType"),
    };

    this.remoteSessionHomeTechUrl = this.resourceService.getPortalSetting("URL_External_PTPScheduling").Value;
    this.remoteSessionMobileUrl = this.resourceService.getPortalSetting("URL_External_P360Scheduling").Value;

    window.scrollTo(0, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  getSetting(name: string): string {
    return this.resourceService.getPortalSetting(name).Value
  }

  setVisibility(dealerCode: string) {
    let isEnabled = this.loggedInUserSettings.isFeatureEnabled(dealerCode, TECHSERVICES);
    if (!this.loggedInUserSettings.isFeatureEnabled(dealerCode, CHATGROUP) && !this.loggedInUserSettings.isFeatureEnabled(dealerCode, TECHGROUP)) {
      isEnabled = false;
    }

    if (isEnabled) {
      this.isLoaded = true;
      this.isTechServicesChatEnabled = this.loggedInUserSettings.isFeatureEnabled(dealerCode, CHATGROUP)
        && this.loggedInUserSettings.isFeatureEnabled(dealerCode, CHAT);
      this.isTechServicesSpecialOffersEnabled = this.loggedInUserSettings.isFeatureEnabled(dealerCode, TECHGROUP);

      if (this.isTechServicesSpecialOffersEnabled && this.offers?.length) {
        let offerList = [];
        // Only include Offers which have are enabled for the dealer code.
        this.offers.forEach(offer => {
          if (this.loggedInUserSettings.isFeatureEnabled(dealerCode, `${offer}_Enabled_ForDealerCode`)) {
            offerList.push(offer);
          }
        });

        if(this.isSPOEEnabled){
          if(this.hasP360MobilePolicy && this.hasP360HomeTechPolicy){
            let tempOffers: string[] = this.mobileOffers.concat(offerList);
            this.offers = tempOffers;
          }
          else if(this.hasP360MobilePolicy && !this.hasP360HomeTechPolicy){
            this.offers = this.mobileOffers;
          }
          else if(!this.hasP360MobilePolicy && this.hasP360HomeTechPolicy){
            this.offers = offerList;
          }
        }
        else{
          this.offers = offerList;
        }

      }
    } else {
      this.featureGuard.canActivate(dealerCode, TECHSERVICES, isEnabled);
    }
  }

  redirectToPTPSchedulingUrl(): void {
    window.open(this.remoteSessionHomeTechUrl, '_blank');
  }

  redirectToP360SchedulingUrl(): void {
    window.open(this.remoteSessionMobileUrl, '_blank');
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
